import React from 'react';
import { Mic, FileText, Activity, Heart, Stethoscope } from 'lucide-react';

const IsometricTranscriptionPhone = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />
                    </div>
                </div>

                {/* Surface Elements (Flat on the board) - MORE BOXES */}

                {/* Orange Card (Top Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '20px',
                        top: '20px',
                        width: '90px',
                        height: '90px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-orange-900" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-600 rounded-2xl flex items-center justify-center shadow-sm border border-orange-400">
                        <FileText className="w-8 h-8 text-orange-200" />
                    </div>
                </div>

                {/* Green Card (Top Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '30px',
                        top: '40px',
                        width: '70px',
                        height: '70px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-green-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-green-500 rounded-2xl flex items-center justify-center shadow-sm border border-green-400">
                        <Activity className="w-8 h-8 text-green-100" />
                    </div>
                </div>

                {/* Red Card (Bottom Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '30px',
                        bottom: '40px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-red-900" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-red-500 rounded-2xl flex items-center justify-center shadow-sm border border-red-400">
                        <Heart className="w-8 h-8 text-red-100" />
                    </div>
                </div>

                {/* Orange Card (Bottom Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '20px',
                        bottom: '20px',
                        width: '100px',
                        height: '70px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-orange-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-500 rounded-2xl flex items-center justify-center shadow-sm border border-orange-400">
                        <div className="w-16 h-3 bg-orange-200/50 rounded-full"></div>
                    </div>
                </div>

                {/* Small Yellow Card (Center Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '10px',
                        top: '130px',
                        width: '50px',
                        height: '50px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-yellow-700" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-yellow-400 rounded-xl flex items-center justify-center shadow-sm border border-yellow-300">
                        <div className="w-6 h-6 rounded-full bg-yellow-200/50" />
                    </div>
                </div>


                {/* Floating Elements - Parallel to Base (Isometric) */}

                {/* Stethoscope Bubble (Back) */}
                <div
                    className="absolute"
                    style={{
                        left: '120px',
                        top: '-10px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(60px) rotateZ(-3deg) rotateX(-90deg)', // Standing up
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-gray-100" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-indigo-50">
                        <Stethoscope className="w-8 h-8 text-indigo-500" />
                    </div>
                </div>


                {/* CELL PHONE (Replacing the Green Phone Circle) */}
                {/* Standing up slightly or angled */}
                <div
                    className="absolute"
                    style={{
                        left: '10%',
                        top: '100%',
                        width: '100px',
                        height: '180px', // Phone aspect ratio
                        marginLeft: '-50px',
                        marginTop: '-90px',
                        transform: 'translateZ(130px) rotateZ(-65deg) rotateX(-90deg) rotateY(25deg)', // Angled phone
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Phone Body Thickness (Black/Dark Grey) */}
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-[20px] bg-gray-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Phone Front Face */}
                    <div className="absolute inset-0 bg-gray-900 rounded-[20px] shadow-2xl border-[4px] border-gray-700 flex flex-col items-center justify-center overflow-hidden">
                        {/* Screen Content */}
                        <div className="w-full h-full bg-gray-800 flex flex-col items-center justify-center relative">
                            {/* Dynamic Island / Notch */}
                            <div className="absolute top-2 w-20 h-6 bg-black rounded-full z-10"></div>

                            {/* Recording UI */}
                            {/* Recording UI */}
                            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center animate-pulse mt-8">
                                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
                                    <Mic className="w-5 h-5 text-white" />
                                </div>
                            </div>

                            {/* Waveform lines */}
                            <div className="mt-6 flex gap-1 items-end h-8">
                                <div className="w-1 bg-red-400 h-3 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-1 bg-red-400 h-6 rounded-full animate-pulse" style={{ animationDelay: '100ms' }}></div>
                                <div className="w-1 bg-red-400 h-4 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                <div className="w-1 bg-red-400 h-7 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-1 bg-red-400 h-5 rounded-full animate-pulse" style={{ animationDelay: '50ms' }}></div>
                                <div className="w-1 bg-red-400 h-3 rounded-full animate-pulse" style={{ animationDelay: '250ms' }}></div>
                            </div>

                            <div className="mt-4 text-[10px] text-gray-400 font-mono">00:42</div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IsometricTranscriptionPhone;
